import { useState, useMemo } from "react";
// Constantes normativas 2026 (Perú)
const RMV_2026 = 1130;
const ASIGNACION_FAMILIAR = RMV_2026 * 0.10;
const TASA_ONP = 0.13;
const PRIMA_SEGURO_AFP = 0.0137;
const APORTE_OBLIGATORIO_AFP = 0.10;

const AFPS = [
  { id: "habitat", nombre: "Habitat", comisionFlujo: 0.0147 },
  { id: "integra", nombre: "Integra", comisionFlujo: 0.0155 },
  { id: "prima", nombre: "Prima", comisionFlujo: 0.0160 },
  { id: "profuturo", nombre: "Profuturo", comisionFlujo: 0.0169 },
];

// Factores por tipo de empresa (Régimen General vs. Ley MYPE - D.S. 013-2013-PRODUCE)
const REGIMENES = {
  general: {
    nombre: "Régimen General",
    tieneCts: true,
    ctsFactor: 1,
    gratificacionFactor: 1,
    vacacionesDiasAnual: 30,
    indemnizacion: { tipo: "sueldos", factorPorAño: 1.5, tope: 12 },
  },
  pequena: {
    nombre: "Pequeña Empresa",
    tieneCts: true,
    ctsFactor: 0.5,
    gratificacionFactor: 0.5,
    vacacionesDiasAnual: 15,
    indemnizacion: { tipo: "dias", factorDiasPorAño: 20, tope: 120 },
  },
  micro: {
    nombre: "Microempresa",
    tieneCts: false,
    ctsFactor: 0,
    gratificacionFactor: 0,
    vacacionesDiasAnual: 15,
    indemnizacion: { tipo: "dias", factorDiasPorAño: 10, tope: 90 },
  },
};

const MOTIVOS_CESE = [
  { id: "renuncia", etiqueta: "Renuncia voluntaria" },
  { id: "despido_arbitrario", etiqueta: "Despido arbitrario" },
  { id: "despido_nulo", etiqueta: "Despido nulo" },
  { id: "mutuo_disenso", etiqueta: "Mutuo disenso" },
  { id: "vencimiento", etiqueta: "Vencimiento de contrato" },
];

function formatearSoles(valor) {
  return valor.toLocaleString("es-PE", {
    style: "currency",
    currency: "PEN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function diferenciaFechas(inicioStr, finStr) {
  if (!inicioStr || !finStr) return { años: 0, meses: 0, dias: 0, dias360: 0 };
  const inicio = new Date(inicioStr + "T00:00:00");
  const fin = new Date(finStr + "T00:00:00");
  if (isNaN(inicio) || isNaN(fin) || fin <= inicio) {
    return { años: 0, meses: 0, dias: 0, dias360: 0 };
  }
  let años = fin.getFullYear() - inicio.getFullYear();
  let meses = fin.getMonth() - inicio.getMonth();
  let dias = fin.getDate() - inicio.getDate();
  if (dias < 0) {
    meses -= 1;
    const mesAnterior = new Date(fin.getFullYear(), fin.getMonth(), 0);
    dias += mesAnterior.getDate();
  }
  if (meses < 0) {
    años -= 1;
    meses += 12;
  }
  const dias360 = años * 360 + meses * 30 + Math.min(dias, 30);
  return { años, meses, dias, dias360 };
}

function inicioPeriodoCTS(fechaCeseStr) {
  const d = new Date(fechaCeseStr + "T00:00:00");
  const mes = d.getMonth() + 1;
  const año = d.getFullYear();
  if (mes >= 1 && mes <= 4) return new Date(año - 1, 10, 1);
  if (mes >= 5 && mes <= 10) return new Date(año, 4, 1);
  return new Date(año, 10, 1);
}

function inicioPeriodoGratificacion(fechaCeseStr) {
  const d = new Date(fechaCeseStr + "T00:00:00");
  const mes = d.getMonth() + 1;
  const año = d.getFullYear();
  if (mes >= 1 && mes <= 6) return new Date(año, 0, 1);
  return new Date(año, 6, 1);
}

function inicioRecordVacacional(fechaInicioStr, fechaCeseStr) {
  const inicio = new Date(fechaInicioStr + "T00:00:00");
  const cese = new Date(fechaCeseStr + "T00:00:00");
  let aniversario = new Date(cese.getFullYear(), inicio.getMonth(), inicio.getDate());
  if (aniversario > cese) {
    aniversario = new Date(cese.getFullYear() - 1, inicio.getMonth(), inicio.getDate());
  }
  if (aniversario < inicio) aniversario = inicio;
  return aniversario;
}

function toISO(date) {
  return date.toISOString().slice(0, 10);
}

export default function CalculadoraLiquidacion() {
  const [tipoEmpresa, setTipoEmpresa] = useState("general");
  const [fechaInicio, setFechaInicio] = useState("2023-01-15");
  const [fechaCese, setFechaCese] = useState("2026-08-01");
  const [sueldoBruto, setSueldoBruto] = useState(3000);
  const [tieneHijos, setTieneHijos] = useState(false);

  const [tipoContrato, setTipoContrato] = useState("indefinido");
  const [fechaFinContrato, setFechaFinContrato] = useState("");

  const [motivoCese, setMotivoCese] = useState("renuncia");

  const [tieneVacacionesPendientes, setTieneVacacionesPendientes] = useState(false);
  const [diasVacacionesPendientes, setDiasVacacionesPendientes] = useState(0);

  const [sistemaPension, setSistemaPension] = useState("afp");
  const [afpId, setAfpId] = useState("habitat");

  const resultado = useMemo(() => {
    const regimen = REGIMENES[tipoEmpresa];
    const bruto = Number(sueldoBruto) || 0;
    const asignacion = tieneHijos ? ASIGNACION_FAMILIAR : 0;
    const remuneracionComputable = bruto + asignacion;
    const remuneracionDiaria = remuneracionComputable / 30;

    const antiguedad = diferenciaFechas(fechaInicio, fechaCese);

    let ctsTrunca = 0;
    let diasCtsComputables = 0;
    if (regimen.tieneCts && fechaInicio && fechaCese) {
      const inicioPeriodo = inicioPeriodoCTS(fechaCese);
      const inicioReal =
        new Date(fechaInicio + "T00:00:00") > inicioPeriodo ? fechaInicio : toISO(inicioPeriodo);
      const diffCts = diferenciaFechas(inicioReal, fechaCese);
      diasCtsComputables = Math.min(180, diffCts.dias360);
      const sextoGratificacion = remuneracionComputable / 6;
      const baseComputableCts = remuneracionComputable + sextoGratificacion;
      ctsTrunca = baseComputableCts * (diasCtsComputables / 360) * regimen.ctsFactor;
    }

    let gratificacionTrunca = 0;
    let diasGratComputables = 0;
    if (regimen.gratificacionFactor > 0 && fechaInicio && fechaCese) {
      const inicioPeriodo = inicioPeriodoGratificacion(fechaCese);
      const inicioReal =
        new Date(fechaInicio + "T00:00:00") > inicioPeriodo ? fechaInicio : toISO(inicioPeriodo);
      const diffGrat = diferenciaFechas(inicioReal, fechaCese);
      diasGratComputables = Math.min(180, diffGrat.dias360);
      gratificacionTrunca =
        remuneracionComputable * (diasGratComputables / 180) * regimen.gratificacionFactor;
    }

    let vacacionesTruncas = 0;
    let diasVacComputables = 0;
    if (fechaInicio && fechaCese) {
      const inicioRecord = inicioRecordVacacional(fechaInicio, fechaCese);
      const diffVac = diferenciaFechas(toISO(inicioRecord), fechaCese);
      diasVacComputables = Math.min(360, diffVac.dias360);
      vacacionesTruncas = remuneracionComputable * (diasVacComputables / 360);
    }

    const diasPendientes = tieneVacacionesPendientes
      ? Math.max(0, Number(diasVacacionesPendientes) || 0)
      : 0;
    const vacacionesPendientes = remuneracionDiaria * diasPendientes;

    let indemnizacion = 0;
    let detalleIndemnizacion = "";

    const esPlazoFijoAnticipado =
      tipoContrato === "plazo_fijo" &&
      fechaFinContrato &&
      new Date(fechaFinContrato + "T00:00:00") > new Date(fechaCese + "T00:00:00") &&
      motivoCese === "despido_arbitrario";

    if (motivoCese === "despido_arbitrario" || motivoCese === "despido_nulo") {
      let base = 0;
      if (esPlazoFijoAnticipado) {
        const restante = diferenciaFechas(fechaCese, fechaFinContrato);
        const mesesRestantes = restante.dias360 / 30;
        base = Math.min(remuneracionComputable * mesesRestantes, remuneracionComputable * 12);
        detalleIndemnizacion = "Remuneraciones dejadas de percibir hasta el fin del contrato (tope 12)";
      } else if (regimen.indemnizacion.tipo === "sueldos") {
        base = Math.min(
          remuneracionComputable * regimen.indemnizacion.factorPorAño * (antiguedad.dias360 / 360),
          remuneracionComputable * regimen.indemnizacion.tope
        );
        detalleIndemnizacion = `${regimen.indemnizacion.factorPorAño} sueldo(s) por año, tope ${regimen.indemnizacion.tope} sueldos`;
      } else {
        base = Math.min(
          remuneracionDiaria * regimen.indemnizacion.factorDiasPorAño * (antiguedad.dias360 / 360),
          remuneracionDiaria * regimen.indemnizacion.tope
        );
        detalleIndemnizacion = `${regimen.indemnizacion.factorDiasPorAño} días por año, tope ${regimen.indemnizacion.tope} días`;
      }
      indemnizacion = motivoCese === "despido_nulo" ? base * 2 : base;
      if (motivoCese === "despido_nulo") {
        detalleIndemnizacion += " (referencial x2; también puede optarse por reposición)";
      }
    }

    const basePensionable = gratificacionTrunca + vacacionesTruncas + vacacionesPendientes;
    let descuentoPension = 0;
    let etiquetaPension = "";
    if (sistemaPension === "onp") {
      descuentoPension = basePensionable * TASA_ONP;
      etiquetaPension = "ONP (13%)";
    } else {
      const afp = AFPS.find((a) => a.id === afpId) ?? AFPS[0];
      const tasaTotal = APORTE_OBLIGATORIO_AFP + PRIMA_SEGURO_AFP + afp.comisionFlujo;
      descuentoPension = basePensionable * tasaTotal;
      etiquetaPension = `${afp.nombre} (${(tasaTotal * 100).toFixed(2)}%)`;
    }

    const totalBruto =
      ctsTrunca + gratificacionTrunca + vacacionesTruncas + vacacionesPendientes + indemnizacion;
    const totalNeto = totalBruto - descuentoPension;

    return {
      regimen,
      antiguedad,
      remuneracionComputable,
      ctsTrunca,
      diasCtsComputables,
      gratificacionTrunca,
      diasGratComputables,
      vacacionesTruncas,
      diasVacComputables,
      vacacionesPendientes,
      diasPendientes,
      indemnizacion,
      detalleIndemnizacion,
      descuentoPension,
      etiquetaPension,
      basePensionable,
      totalNeto,
    };
  }, [
    tipoEmpresa,
    fechaInicio,
    fechaCese,
    sueldoBruto,
    tieneHijos,
    tipoContrato,
    fechaFinContrato,
    motivoCese,
    tieneVacacionesPendientes,
    diasVacacionesPendientes,
    sistemaPension,
    afpId,
  ]);

  const inputClase =
    "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700";
  const botonClase = (activo) =>
    `rounded-lg border py-2.5 px-2 text-xs sm:text-sm font-medium transition-colors ${
      activo
        ? "border-red-700 bg-red-50 text-red-800"
        : "border-slate-300 text-slate-600 hover:bg-slate-50 bg-white"
    }`;

  return (
    <div className="w-full min-h-full px-4 py-10 bg-stone-50">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center text-lg text-white bg-red-700 rounded-full h-11 w-11">
            📋
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">
              Calculadora de Liquidación
            </h1>
            <p className="text-sm text-slate-500">Perú 2026 · CTS, gratificación, vacaciones e indemnización</p>
          </div>
        </div>

        <div className="overflow-hidden bg-white border shadow-sm rounded-2xl border-slate-200">
          <div className="p-6 space-y-5 border-b border-slate-100">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Tipo de empresa
              </label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(REGIMENES).map(([id, r]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setTipoEmpresa(id)}
                    className={botonClase(tipoEmpresa === id)}
                  >
                    {r.nombre.replace("Empresa", "").trim()}
                  </button>
                ))}
              </div>
              <p className="mt-1.5 text-xs text-slate-400">
                {tipoEmpresa === "micro" &&
                  "REMYPE: sin CTS ni gratificación legal, 15 días de vacaciones."}
                {tipoEmpresa === "pequena" &&
                  "REMYPE: CTS y gratificación reducidas a la mitad, 15 días de vacaciones."}
                {tipoEmpresa === "general" && "Beneficios completos según D.L. 728."}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Fecha de inicio
                </label>
                <input
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                  className={inputClase}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Fecha de cese
                </label>
                <input
                  type="date"
                  value={fechaCese}
                  onChange={(e) => setFechaCese(e.target.value)}
                  className={inputClase}
                />
              </div>
            </div>
            <p className="-mt-3 text-xs text-slate-400">
              Antigüedad: {resultado.antiguedad.años} año(s), {resultado.antiguedad.meses} mes(es),{" "}
              {resultado.antiguedad.dias} día(s)
            </p>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Sueldo bruto mensual
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">
                  S/
                </span>
                <input
                  type="number"
                  min="0"
                  value={sueldoBruto}
                  onChange={(e) => setSueldoBruto(e.target.value)}
                  className={`${inputClase} pl-9 font-mono text-lg`}
                  placeholder="3000"
                />
              </div>
            </div>

            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={tieneHijos}
                onChange={(e) => setTieneHijos(e.target.checked)}
                className="w-4 h-4 text-red-700 rounded border-slate-300 focus:ring-red-700/30"
              />
              <span className="text-sm text-slate-700">
                Recibo asignación familiar ({formatearSoles(ASIGNACION_FAMILIAR)})
              </span>
            </label>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Tipo de contrato
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setTipoContrato("indefinido")}
                  className={botonClase(tipoContrato === "indefinido")}
                >
                  Indefinido
                </button>
                <button
                  type="button"
                  onClick={() => setTipoContrato("plazo_fijo")}
                  className={botonClase(tipoContrato === "plazo_fijo")}
                >
                  Plazo fijo
                </button>
              </div>
            </div>

            {tipoContrato === "plazo_fijo" && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Fecha de fin de contrato pactada
                </label>
                <input
                  type="date"
                  value={fechaFinContrato}
                  onChange={(e) => setFechaFinContrato(e.target.value)}
                  className={inputClase}
                />
                <p className="mt-1 text-xs text-slate-400">
                  Solo se usa si hay despido arbitrario antes del vencimiento pactado.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Motivo de cese
              </label>
              <select
                value={motivoCese}
                onChange={(e) => setMotivoCese(e.target.value)}
                className={inputClase}
              >
                {MOTIVOS_CESE.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.etiqueta}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="flex items-center gap-2.5 cursor-pointer select-none mb-2">
                <input
                  type="checkbox"
                  checked={tieneVacacionesPendientes}
                  onChange={(e) => setTieneVacacionesPendientes(e.target.checked)}
                  className="w-4 h-4 text-red-700 rounded border-slate-300 focus:ring-red-700/30"
                />
                <span className="text-sm text-slate-700">
                  Tengo vacaciones ya devengadas y no gozadas
                </span>
              </label>
              {tieneVacacionesPendientes && (
                <input
                  type="number"
                  min="0"
                  max="30"
                  value={diasVacacionesPendientes}
                  onChange={(e) => setDiasVacacionesPendientes(e.target.value)}
                  className={inputClase}
                  placeholder="Días pendientes"
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Sistema de pensión
              </label>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => setSistemaPension("afp")}
                  className={botonClase(sistemaPension === "afp")}
                >
                  AFP
                </button>
                <button
                  type="button"
                  onClick={() => setSistemaPension("onp")}
                  className={botonClase(sistemaPension === "onp")}
                >
                  ONP
                </button>
              </div>
              {sistemaPension === "afp" && (
                <select
                  value={afpId}
                  onChange={(e) => setAfpId(e.target.value)}
                  className={inputClase}
                >
                  {AFPS.map((afp) => (
                    <option key={afp.id} value={afp.id}>
                      {afp.nombre} — comisión {(afp.comisionFlujo * 100).toFixed(2)}%
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="p-6 bg-slate-900">
            <p className="mb-1 text-xs tracking-wide uppercase text-slate-400">
              Liquidación neta estimada
            </p>
            <p className="mb-5 font-mono text-4xl font-bold text-white">
              {formatearSoles(Math.max(0, resultado.totalNeto))}
            </p>

            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-300">
                <span>CTS trunca ({resultado.diasCtsComputables} días)</span>
                <span className="font-mono">{formatearSoles(resultado.ctsTrunca)}</span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>Gratificación trunca ({resultado.diasGratComputables} días)</span>
                <span className="font-mono">{formatearSoles(resultado.gratificacionTrunca)}</span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>Vacaciones truncas ({resultado.diasVacComputables} días)</span>
                <span className="font-mono">{formatearSoles(resultado.vacacionesTruncas)}</span>
              </div>
              {resultado.diasPendientes > 0 && (
                <div className="flex justify-between text-slate-300">
                  <span>Vacaciones pendientes ({resultado.diasPendientes} días)</span>
                  <span className="font-mono">{formatearSoles(resultado.vacacionesPendientes)}</span>
                </div>
              )}
              {resultado.indemnizacion > 0 && (
                <div className="flex justify-between text-slate-300">
                  <span>+ Indemnización</span>
                  <span className="font-mono">{formatearSoles(resultado.indemnizacion)}</span>
                </div>
              )}
              {resultado.descuentoPension > 0 && (
                <div className="flex justify-between text-slate-400 border-t border-slate-700 pt-1.5 mt-1.5">
                  <span>− {resultado.etiquetaPension} (sobre grat. + vacaciones)</span>
                  <span className="font-mono">−{formatearSoles(resultado.descuentoPension)}</span>
                </div>
              )}
            </div>
            {resultado.indemnizacion > 0 && (
              <p className="mt-3 text-xs text-slate-500">{resultado.detalleIndemnizacion}</p>
            )}
          </div>
        </div>

        <p className="mt-4 text-xs leading-relaxed text-slate-400">
          Herramienta orientativa. La CTS y la indemnización no están afectas a
          descuentos de AFP/ONP; la gratificación y las vacaciones sí, por ser
          conceptos remunerativos. No incluye retención de renta de 5ta categoría que
          pudiera corresponder sobre gratificación o vacaciones si aún no superaste el
          tramo exonerado del año. En "renuncia voluntaria", "mutuo disenso" y
          "vencimiento de contrato" no corresponde indemnización, salvo negociación
          particular. No constituye asesoría legal, contable ni laboral.
        </p>
      </div>
    </div>
  );
}